import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api_url, DEFAULT_LOGO_SRC } from "../../constant/constant";

const UserAssessment = () => {
  const { t } = useTranslation();

  const [questions, setQuestions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [error, setError] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const questionArr = localStorage.getItem("questions");

    if (!questionArr) {
      setError(t("Noquestionsfound") || "No questions found.");
      return;
    }

    try {
      const parsedQuestions = JSON.parse(questionArr).map((question) => ({
        ...question,
        options: Array.isArray(question.options)
          ? question.options.map((option) => ({
              ...option,
              text: option.option_arabic || option.option || "",
            }))
          : [],
      }));

      setQuestions(parsedQuestions);
    } catch (e) {
      console.error("Error parsing questions from localStorage", e);
      setError(t("FailedtosubmitassessmentPleasetryagainlater"));
    }
  }, [t]);

  const readJson = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
      throw new Error("API returned HTML instead of JSON. Check the API route URL.");
    }

    return response.json();
  };

  const handleNextQuestion = () => {
    if (!selectedOption) {
      setError(t("Please select an option."));
      return;
    }

    const currentQuestion = questions[currentQuestionIndex];

    if (!currentQuestion) {
      setError(t("somethingwentwrong") || "Something went wrong.");
      return;
    }

    setError("");

    const newAnswer = {
      question_id: currentQuestion.question_id,
      option_id: selectedOption,
    };

    if (currentQuestionIndex < questions.length - 1) {
      setAnswers((prevAnswers) => [...prevAnswers, newAnswer]);
      setSelectedOption("");
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
      return;
    }

    const finalAnswers = [...answers, newAnswer];
    setAnswers(finalAnswers);
    setIsCompleted(true);
    submitAssessment(finalAnswers);
  };

  const submitAssessment = async (finalAnswers) => {
    setIsLoading(true);
    setError("");

    const token = localStorage.getItem("userToken");

    if (!token) {
      setError(t("Usernotauthenticated.") || "User not authenticated.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${api_url}/submit-assessment`, {
        mode: "cors",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          answers: finalAnswers,
        }),
      });

      const data = await readJson(response);

      if (!response.ok) {
        throw new Error(data?.message || t("FailedtosubmitassessmentPleasetryagainlater"));
      }

      setResult({
        message: data?.message || t("Theassessmentissubmittedsuccessfully."),
        score: data?.score || "0/0",
        awareness_score_percentage: data?.awareness_score_percentage || 0,
      });

      localStorage.removeItem("questions");
    } catch (error) {
      console.error("Error submitting assessment:", error);

      setResult({
        message: error.message || t("Error submitting assessment. Please try again later."),
        score: "0/0",
        awareness_score_percentage: 0,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const gotoDashboard = () => {
    const token = localStorage.getItem("userToken");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token) {
      window.location = "/login";
      return;
    }

    if (userData?.role_id === 5 || String(userData?.role_id) === "5") {
      window.location = "/user/dashboard";
      return;
    }

    window.location = "/dashboard";
  };

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="container">
      <div
        className="row"
        style={{
          height: "100vh",
          alignItems: "center",
          flexDirection: "column",
          marginTop: "100px",
        }}
      >
        <div className="loginLogo mt-5" align="center">
          <img src={DEFAULT_LOGO_SRC} alt="Cyber Sense" style={{ width: "160px" }} />
        </div>

        <div className="col-md-6">
          <div
            className="assessmentBox card mt-5 p-3"
            style={{ borderRadius: "30px" }}
            align="center"
          >
            <div className="card-body">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-dark" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">{t("Submittingyouranswers...")}</p>
                </div>
              ) : (
                <>
                  {!isCompleted && (
                    <h6 className="questionTitle">{t("Pleaseanswerthefollowingquestion")}</h6>
                  )}

                  {error && <div className="alert alert-danger mx-5">{error}</div>}

                  {questions.length > 0 && !isCompleted && currentQuestion && (
                    <div key={currentQuestion.question_id} className="mb-4">
                      <h6>{currentQuestion.question_arabic || currentQuestion.question}</h6>

                      <div className="options mt-3">
                        {currentQuestion.options.map((option, optionIndex) => (
                          <div key={option.option_id || optionIndex} className="form-check">
                            <input
                              type="radio"
                              className="form-check-input"
                              id={`option-${optionIndex}`}
                              name="options"
                              value={option.option_id}
                              style={{
                                height: "18px",
                                width: "18px",
                                border: "1px solid grey",
                              }}
                              checked={selectedOption === option.option_id}
                              onChange={() => setSelectedOption(option.option_id)}
                            />

                            <label className="form-check-label" htmlFor={`option-${optionIndex}`}>
                              {option.option_arabic || option.option}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isCompleted && result && (
                    <div className="result mt-4">
                      <h6>{t("AssessmentResult")}</h6>

                      <p>{result.message}</p>

                      <p>
                        {t("Score")}: {result.score}
                      </p>

                      <p>
                        {t("AwarenessScorePercentage")}: {result.awareness_score_percentage}%
                      </p>

                      <button
                        type="button"
                        className="btn btn-dark w-100 p-2 mt-4"
                        onClick={gotoDashboard}
                      >
                        {t("Next")}
                      </button>
                    </div>
                  )}

                  {!isCompleted && !isLoading && questions.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-dark w-100 p-2 mt-4"
                      onClick={handleNextQuestion}
                    >
                      {t("Next")}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAssessment;